import { useCallback, useEffect } from 'react';
import { useHydrated } from 'remix-utils';
import { useTracker } from '@archipro-website/tracker';
import {
    TWO_PANE_SURFACE_SIZE_CLASSNAME,
    TwoPaneSurface,
} from '@rocco/components/two-pane-modal';
import { cn } from '@rocco/utils/cn';
import { GATE_PANE_BRAND_DEFAULT } from '~/modules-rocco/user/auth-gate/gate-pane-image';
import { isReturningPasskeyDevice } from '~/modules/user/passkey/passkey-device-match';
import type { LoginFlow } from '~/modules/user/hook/use-login-flow';
import type { AuthSource } from '~/modules/user/type';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import { AuthModalBrandPane } from './AuthModalBrandPane';
import { AuthPanel } from './AuthPanel';
import { getAuthBrandStatement, getAuthIntentCopy } from './auth-intent-copy';

export interface AuthPageCardProps {
    authSource: AuthSource;
    flow?: LoginFlow;
    redirect?: string;
    allowExternalUrls?: boolean;
    skipPostSignUp?: boolean;
    /** Overrides the intent registry headline. */
    header?: string;
    /** Overrides the intent registry subcopy. */
    subtitle?: string;
    /** Contextual line shown above the form (e.g. the 401 page's reason). */
    message?: string;
    defaultPublicUser?: PublicUser;
    className?: string;
    onAuthSuccess?: () => void;
}

/**
 * The auth card as a page region rather than a dialog — what `/login` renders.
 *
 * Same two panes, same step machine, same intent copy as `AuthModal`, because
 * they are the same components: only the container differs. A page has nothing
 * to dismiss, so there is no close control and no `open` state; the route
 * decides who sees the page at all.
 *
 * Impressions are logged here as well as in the modal. The page is a real entry
 * point — every server-side member-only redirect lands on it — and leaving it
 * out would make it the one auth surface missing from the funnel.
 */
export const AuthPageCard = (props: AuthPageCardProps) => {
    const {
        authSource,
        flow,
        redirect,
        allowExternalUrls,
        skipPostSignUp,
        header,
        subtitle,
        message,
        defaultPublicUser,
        className,
        onAuthSuccess,
    } = props;

    const tracker = useTracker();
    const intentCopy = getAuthIntentCopy(authSource);

    /**
     * The returning-device copy waits for hydration, because the device hint it
     * reads lives in localStorage and the server cannot see it.
     *
     * In the modal this is safe to read while rendering — a dialog only ever
     * opens on the client. A page is server-rendered, so the same call would
     * have the server commit to the first-visit statement and the client's
     * first render disagree on exactly the devices the copy is for. React
     * treats that as a failed hydration and gives up on the tree, which on this
     * route is the whole card.
     */
    const hydrated = useHydrated();
    const returningDevice = hydrated && isReturningPasskeyDevice();

    // Stable identity: AuthPanel keeps this in callback deps, and a fresh arrow
    // each render would rebuild them for nothing.
    const handleAuthSuccess = useCallback(() => {
        onAuthSuccess?.();
    }, [onAuthSuccess]);

    useEffect(() => {
        tracker.log('AuthModalImpression', {
            url: new URL(window.location.href),
            data: { Source: authSource },
            ga4Data: { source: authSource },
            targetTracker: ['archiproTracker', 'ga4Tracker'],
        });
    }, [authSource, tracker]);

    return (
        <section
            aria-label={header || intentCopy.headline}
            className={cn(
                'w-full overflow-hidden bg-white',
                // Rounded at every size, unlike the modal. The modal is square
                // below `md` because it goes full-screen there and the overlay
                // would show through a clipped corner; this card is inset on
                // the page backdrop at every width, so a square one reads as
                // unfinished rather than as edge-to-edge.
                'rounded-2.5 md:rounded-5',
                TWO_PANE_SURFACE_SIZE_CLASSNAME,
                className,
            )}
        >
            <TwoPaneSurface
                corners="rounded"
                bodyPadding="roomy"
                pane={
                    // No item stands behind a plain sign-in, so brand imagery is
                    // the intended answer here rather than a fallback.
                    <AuthModalBrandPane
                        statement={getAuthBrandStatement(
                            authSource,
                            returningDevice,
                        )}
                        image={GATE_PANE_BRAND_DEFAULT}
                    />
                }
            >
                <AuthPanel
                    authSource={authSource}
                    flow={flow}
                    redirect={redirect}
                    allowExternalUrls={allowExternalUrls}
                    skipPostSignUp={skipPostSignUp}
                    header={header}
                    subtitle={subtitle}
                    message={message}
                    defaultPublicUser={defaultPublicUser}
                    onAuthSuccess={handleAuthSuccess}
                />
            </TwoPaneSurface>
        </section>
    );
};
